import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { getComicBySlug, getCategoryBySlug, comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const comic = getComicBySlug(slug || '');
  const category = comic ? getCategoryBySlug(comic.category) : undefined;

  const relatedComics = comic
    ? comics
        .filter((c) => c.category === comic.category && c.id !== comic.id)
        .slice(0, 5)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto text-4xl">
              <i className="ri-emotion-sad-line text-foreground-400"></i>
            </span>
            <p className="mt-4 text-foreground-600">未找到该漫画</p>
            <Link
              to="/"
              className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabels: Record<string, string> = {
    korean: '韩漫',
    japanese: '日漫',
    chinese: '国漫',
    series: '系列漫画',
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              {category && (
                <>
                  <Link
                    to={`/category/${comic.category}`}
                    className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {category.name}
                  </Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                </>
              )}
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Detail */}
        <section className="w-full bg-background-50 py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left - Cover */}
              <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 皮皮喵漫画`}
                    title={`${comic.title} - 皮皮喵漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Right - Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {comic.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">
                    作者：{comic.author}
                  </span>
                  <span className="text-sm text-foreground-500">
                    类型：
                    <Link
                      to={`/category/${comic.category}`}
                      className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                    >
                      {categoryLabels[comic.category] || comic.category}
                    </Link>
                  </span>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                    comic.status === 'ongoing'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </span>
                    <strong className="text-foreground-900">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-foreground-400 text-sm"></i>
                    </span>
                    {Math.floor(comic.views / 10000)}万 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-foreground-400 text-sm"></i>
                    </span>
                    共 {comic.chapters} 话
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-foreground-400 text-sm"></i>
                    </span>
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-semibold text-foreground-900">作品简介</h2>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    {comic.longDescription}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      const latest = comic.chaptersList[0];
                      if (latest) setSelectedChapter(latest.id);
                    }}
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </button>
                  <button
                    onClick={() => {
                      const el = document.getElementById('chapter-list');
                      el?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 font-medium text-sm border border-background-300/60 hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer"
                  >
                    查看目录
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section id="chapter-list" className="w-full bg-background-100 py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                章节列表
              </h2>
              <div className="flex items-center gap-1">
                <button className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-secondary-100 text-secondary-700 cursor-pointer">
                  正序
                </button>
                <button className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap text-foreground-500 hover:text-foreground-700 cursor-pointer">
                  倒序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {comic.chaptersList.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => {
                    setSelectedChapter(ch.id);
                  }}
                  className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all cursor-pointer ${
                    selectedChapter === ch.id
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-background-200/70 bg-background-50 hover:border-background-300'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-800 truncate whitespace-nowrap">
                      {ch.title}
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {ch.updatedAt}
                    </p>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-2">
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Reading Modal */}
        {selectedChapter && (
          <div className="fixed inset-0 z-[60] bg-foreground-950/80 flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-lg w-full max-w-lg max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-foreground-900 font-heading">
                  {comic.chaptersList.find((c) => c.id === selectedChapter)?.title}
                </h3>
                <button
                  onClick={() => setSelectedChapter(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg text-foreground-600"></i>
                </button>
              </div>
              <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p className="text-center text-foreground-400 italic py-8">
                  正在加载漫画内容...
                </p>
                <p className="text-center text-xs text-foreground-500">
                  提示：点击右上角关闭按钮返回章节列表。实际使用中此处将展示完整的漫画页面内容。
                </p>
              </div>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setSelectedChapter(null)}
                  className="px-5 py-2 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="w-full bg-background-50 py-8 md:py-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${comic.title} - 皮皮喵漫画`,
            "description": comic.description,
            "author": {
              "@type": "Person",
              "name": comic.author
            },
            "datePublished": comic.updatedAt,
            "dateModified": comic.updatedAt,
            "image": comic.coverImage,
            "publisher": {
              "@type": "Organization",
              "name": "皮皮喵漫画",
              "url": "https://pipimao.com"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://pipimao.com/comic/${comic.slug}`
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://pipimao.com" },
                { "@type": "ListItem", "position": 2, "name": category?.name || comic.category, "item": `https://pipimao.com/category/${comic.category}` },
                { "@type": "ListItem", "position": 3, "name": comic.title, "item": `https://pipimao.com/comic/${comic.slug}` }
              ]
            }
          }),
        }}
      />
    </>
  );
}